import { LANGUAGES } from '../../../constants';
import type { GoogleSheetsContentValue, LanguageCode } from '../../../types';
import { checkLanguageCode } from '../../../utils';
import { normalizePhrase } from './utils';

export type CustomTranslationsMap = Map<string, Map<LanguageCode, string>>;

function normalizeLanguageCode(code: string) {
  return code.trim().toLocaleLowerCase();
}

function buildMappedLanguages(): Map<string, LanguageCode> {
  return new Map(
    Object.entries(LANGUAGES).map(([code, language]) => [
      language.originalName,
      code
    ])
  );
}

function resolveLanguageCode(
  inputCode: string,
  mappedLanguages: Map<string, LanguageCode>
): LanguageCode | null {
  const normalizedInputCode = normalizeLanguageCode(inputCode);
  if (checkLanguageCode(normalizedInputCode)) {
    return normalizedInputCode;
  }

  for (const languageCode of Object.keys(LANGUAGES)) {
    if (normalizeLanguageCode(languageCode) === normalizedInputCode) {
      return languageCode;
    }
  }

  return mappedLanguages.get(inputCode) ?? null;
}

function extractLanguageCode(
  columnIndex: string,
  mappedLanguages: Map<string, LanguageCode>
): LanguageCode | null {
  const match = columnIndex.match(/\(([^()]*)\)/);
  const [_, languageName] = match || [];

  if (!languageName) {
    return null;
  }

  // В заголовках может быть формат "OriginalName - CODE"
  // (например: "汉语 - ZH-CN"), поэтому сначала пробуем достать код.
  const codeFromHeader = languageName.match(/[a-z0-9]+(?:-[a-z0-9]+)+/i)?.[0];
  if (codeFromHeader) {
    const resolvedCode = resolveLanguageCode(codeFromHeader, mappedLanguages);
    if (resolvedCode) {
      return resolvedCode;
    }
  }

  return resolveLanguageCode(languageName, mappedLanguages);
}

export function parseGoogleSheetsData(
  rows: GoogleSheetsContentValue[][]
): CustomTranslationsMap {
  if (!rows?.length) {
    throw new Error('No rows provided');
  }

  const mappedLanguages = buildMappedLanguages();
  const translationsMap: CustomTranslationsMap = new Map();

  for (const row of rows) {
    const languageMap = new Map<LanguageCode, string>();
    const originalPhrase = normalizePhrase(row[0].rowIndex);

    for (const { columnIndex, value } of row) {
      const languageCode = extractLanguageCode(columnIndex, mappedLanguages);
      if (languageCode && value) {
        languageMap.set(languageCode, value.trim());
      }
    }

    translationsMap.set(originalPhrase, languageMap);
  }

  return translationsMap;
}
